import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PublicLayout from '../components/PublicLayout';
import { Mail, Send, MapPin, Phone, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Παρακαλώ συμπληρώστε όλα τα υποχρεωτικά πεδία');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Το μήνυμά σας στάλθηκε επιτυχώς!');
    } catch (error) {
      toast.error('Σφάλμα αποστολής. Παρακαλώ δοκιμάστε ξανά.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLayout>
      {/* Header */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Επικοινωνήστε μαζί μας
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Έχετε απορίες; Θέλετε περισσότερες πληροφορίες; 
            Είμαστε εδώ για να βοηθήσουμε.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Επικοινωνήστε μαζί μας
                </h2>
                <p className="text-slate-600">
                  Συμπληρώστε τη φόρμα και θα επικοινωνήσουμε μαζί σας το συντομότερο δυνατό.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-violet-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Τοποθεσία</h3>
                    <p className="text-slate-600">Ελλάδα</p>
                  </div>
                </div>
              </div>

              {/* Additional Info Box */}
              <div className="p-6 bg-slate-50 rounded-2xl">
                <h3 className="font-semibold text-slate-900 mb-2">Ώρες Εξυπηρέτησης</h3>
                <p className="text-slate-600">
                  Δευτέρα - Παρασκευή: 09:00 - 18:00<br />
                  Απαντάμε σε όλα τα μηνύματα εντός 24 ωρών.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-lg">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                    <Send className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    Ευχαριστούμε!
                  </h3>
                  <p className="text-slate-600 mb-6">
                    Το μήνυμά σας στάλθηκε επιτυχώς. 
                    Θα επικοινωνήσουμε μαζί σας σύντομα.
                  </p>
                  <Button
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({ name: '', company: '', email: '', phone: '', subject: '', message: '' });
                    }}
                    variant="outline"
                    className="rounded-full"
                  >
                    Στείλτε νέο μήνυμα
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-slate-700 font-medium">
                      Ονοματεπώνυμο *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Π.χ. Γιώργος Παπαδόπουλος"
                      className="mt-2 h-12 rounded-lg border-slate-200 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="company" className="text-slate-700 font-medium">
                      Επωνυμία Επιχείρησης
                    </Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Π.χ. ABC Consulting"
                      className="mt-2 h-12 rounded-lg border-slate-200 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-slate-700 font-medium">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="email@example.com"
                      className="mt-2 h-12 rounded-lg border-slate-200 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-slate-700 font-medium">
                      Τηλέφωνο Επικοινωνίας
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Π.χ. 6912345678"
                      className="mt-2 h-12 rounded-lg border-slate-200 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                    />
                  </div>

                  <div>
                    <Label htmlFor="subject" className="text-slate-700 font-medium">
                      Θέμα
                    </Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="Π.χ. Ερώτηση για πακέτα"
                      className="mt-2 h-12 rounded-lg border-slate-200 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-slate-700 font-medium">
                      Μήνυμα *
                    </Label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Γράψτε το μήνυμά σας..."
                      rows={5}
                      className="mt-2 w-full rounded-lg border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 focus:outline-none resize-none"
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-full py-6 bg-slate-900 hover:bg-slate-800 text-white font-medium hover:scale-[1.02] transition-all"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Αποστολή...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Αποστολή Μηνύματος
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default ContactPage;
